import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormHelperText,
  FormLabel,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useDisclosure,
  VStack,
} from "@chakra-ui/react";
import type { ColDef, RowClickedEvent } from "ag-grid-community";
import AsyncCreatableSelect from "react-select/async-creatable";
import BaseTemplate from "../components/BaseTemplate";
import PersistentGrid from "../components/PersistentGrid/PersistentGrid";
import PersistentGridResetButton from "../components/PersistentGrid/PersistentGridResetButton";
import PersistentGridClearFiltersButton from "../components/PersistentGrid/PersistentGridClearFiltersButton";
import { PersistentGridProvider } from "../contexts/PersistentGridContext";
import { useApi } from "../hooks/useApi";
import { NotificationContext } from "../components/NotificationPane";
import type { NotificationContextType, SupplierMapping, SupplierMappingInput } from "../types";
import InternalUserRoute from "../util/internal_user_route";
import { buildDefaultColDef, EDB_CUSTOM_withHeaderTooltips } from "../util/AgGridFunctions";
import { useColumnDefaults } from "../hooks/useColumnDefaults";

// The supplier twin of account_mapping.tsx - same grid + add/edit-modal shape, backed by
// ref.crm_supplier_mapping via dbo.sp_web_grid__supplier_mapping__get / __put.
//
// Differences from the account page, all driven by what this table means:
//  - CRM Supplier is a *creatable* type-ahead over the Supplier accounts in crm_copy.account.
//    Picking one sets crm_supplier (the name) AND supplier_id (crm_copy.account.Id), exactly as
//    the account page sets crm_account + account_id. Typing a name that is not in CRM is allowed
//    too - 2 of the 98 rows on DEV are suppliers with no CRM account - and leaves supplier_id null.
//  - The supplier code is NOT user-entered. CRM is its master (account.eb_account_code): the grid
//    shows it read-only as "CRM Supplier Code" ('' when the account has none) and the put proc
//    copies it into the table itself. There is deliberately no code field on either form.
//  - isPicklist is exposed as a checkbox because main.doc_from_vw filters on it: 'Y' rows are the
//    canonical supplier entries in the file viewer's From picklist, 'N' rows are aliases that
//    stay out of it. New rows default to Y.

type EditableSupplierRow = {
  supplier_alias: string;
  crm_supplier: string;
  supplier_id: string | null;
  isPicklist: boolean;
};

// A CRM account from the type-ahead (supplierId set) or a free-typed name (supplierId null).
type CrmOption = {
  label: string;
  value: string;
  supplierId: string | null;
};

const SUPPLIER_ALIAS_MAX = 50;
const CRM_SUPPLIER_MAX = 50;

const emptyForm = (): EditableSupplierRow => ({
  supplier_alias: "",
  crm_supplier: "",
  supplier_id: null,
  isPicklist: true,
});

// react-select's Creatable hands back {label, value, __isNew__: true} for a typed name; normalise
// that and any CRM option into one shape so the rest of the page never sees __isNew__.
const normaliseCrmOption = (opt: unknown): CrmOption | null => {
  if (!opt) return null;
  const o = opt as Partial<CrmOption> & { __isNew__?: boolean };
  const label = (o.label ?? "").toString();
  if (o.__isNew__ || !o.supplierId) {
    return { label, value: label, supplierId: null };
  }
  return { label, value: o.value ?? o.supplierId, supplierId: o.supplierId };
};

type SupplierMappingFieldsProps = {
  idSuffix: string;
  form: EditableSupplierRow;
  onAliasChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onPicklistChange: (checked: boolean) => void;
  selectedCrm: CrmOption | null;
  onCrmChange: (option: CrmOption | null) => void;
  loadCrmSupplierOptions: (input: string) => Promise<CrmOption[]>;
};

function SupplierMappingFields({
  idSuffix,
  form,
  onAliasChange,
  onPicklistChange,
  selectedCrm,
  onCrmChange,
  loadCrmSupplierOptions,
}: SupplierMappingFieldsProps) {
  return (
    <VStack spacing={3} align="stretch">
      <FormControl isRequired>
        <FormLabel>Supplier Alias</FormLabel>
        <Input
          value={form.supplier_alias}
          onChange={onAliasChange}
          maxLength={SUPPLIER_ALIAS_MAX}
        />
        <FormHelperText>The supplier name as it appears on documents. Must be unique.</FormHelperText>
      </FormControl>

      <FormControl isRequired>
        <FormLabel>CRM Supplier</FormLabel>
        <AsyncCreatableSelect
          inputId={`crmSupplier${idSuffix}`}
          placeholder="Start typing..."
          cacheOptions
          defaultOptions
          loadOptions={loadCrmSupplierOptions}
          getOptionValue={(o) => o.value}
          getOptionLabel={(o) => o.label}
          value={selectedCrm}
          onChange={(opt) => onCrmChange(normaliseCrmOption(opt))}
          formatCreateLabel={(input) => `Use "${input}" (not in CRM)`}
          isClearable={false}
          styles={{ menu: (p) => ({ ...p, zIndex: 10 }) }}
        />
        <FormHelperText>
          {form.supplier_id
            ? `Linked to CRM account ${form.supplier_id}. The supplier code comes from that account.`
            : "Not linked to a CRM account - pick one from the list to link it, or keep a typed name for a supplier that is not in CRM."}
        </FormHelperText>
      </FormControl>

      <FormControl>
        <Checkbox
          isChecked={form.isPicklist}
          onChange={(e) => onPicklistChange(e.target.checked)}
        >
          Show in picklist
        </Checkbox>
        <FormHelperText>
          Tick for the one row that represents this supplier in the file viewer's From list; leave
          unticked for extra aliases.
        </FormHelperText>
      </FormControl>
    </VStack>
  );
}

function SupplierMappingPage() {
  const {
    getSupplierMappings,
    getSuggestedSuppliers,
    updateSupplierMapping,
    addSupplierMapping,
  } = useApi();
  const columnDefaults = useColumnDefaults();
  const { pushNotification } =
    useContext<NotificationContextType | null>(NotificationContext)!;
  const [rowData, setRowData] = useState<SupplierMapping[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const {
    isOpen: isEditOpen,
    onOpen: onEditOpen,
    onClose: onEditClose,
  } = useDisclosure();
  const {
    isOpen: isAddOpen,
    onOpen: onAddOpen,
    onClose: onAddClose,
  } = useDisclosure();
  const [selectedRow, setSelectedRow] = useState<SupplierMapping | null>(null);
  const [selectedCrm, setSelectedCrm] = useState<CrmOption | null>(null);
  const [selectedAddCrm, setSelectedAddCrm] = useState<CrmOption | null>(null);
  const [editForm, setEditForm] = useState<EditableSupplierRow>(emptyForm());
  const [addForm, setAddForm] = useState<EditableSupplierRow>(emptyForm());

  const loadSupplierMappings = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getSupplierMappings();
      setRowData(data);
      setError(null);
    } catch (err) {
      console.error("Error loading supplier mappings", err);
      setError("Failed to load supplier mappings.");
      pushNotification("Failed to load supplier mappings.", "error");
    } finally {
      setLoading(false);
    }
  }, [getSupplierMappings, pushNotification]);

  useEffect(() => {
    loadSupplierMappings();
  }, [loadSupplierMappings]);

  const columnDefs = useMemo<ColDef<SupplierMapping>[]>(
    () => EDB_CUSTOM_withHeaderTooltips([
      { field: "sk", headerName: "SK", ...buildDefaultColDef(columnDefaults, "sk"), minWidth: 100 },
      { field: "supplier_alias", headerName: "Supplier Alias", ...buildDefaultColDef(columnDefaults, "supplier_alias"), minWidth: 180 },
      { field: "crm_supplier", headerName: "CRM Supplier", ...buildDefaultColDef(columnDefaults, "crm_supplier"), minWidth: 180 },
      // Read-only: crm_copy.account.eb_account_code of the linked account, '' when it has none.
      { field: "crm_supplier_code", headerName: "CRM Supplier Code", ...buildDefaultColDef(columnDefaults, "crm_supplier_code"), minWidth: 160 },
      { field: "isPicklist", headerName: "Is Picklist", ...buildDefaultColDef(columnDefaults, "isPicklist"), minWidth: 140 },
      { field: "supplier_id", headerName: "CRM Account ID", ...buildDefaultColDef(columnDefaults, "supplier_id"), minWidth: 300 },
    ]),
    [columnDefaults],
  );

  const defaultColDef = useMemo<ColDef<SupplierMapping>>(
    () => ({
      ...buildDefaultColDef(columnDefaults),
      sortable: true,
      resizable: true,
      filter: true,
      floatingFilter: true,
      minWidth: 120,
      flex: 1,
    }),
    [columnDefaults],
  );

  const loadCrmSupplierOptions = async (input: string): Promise<CrmOption[]> => {
    if (!input || input.trim().length < 2) return [];
    const matches = await getSuggestedSuppliers(input.trim());
    return matches.map(({ supplier_id, supplier_name }) => ({
      label: supplier_name,
      value: supplier_id,
      supplierId: supplier_id,
    }));
  };

  const applyCrmChoice = (
    option: CrmOption | null,
    setForm: React.Dispatch<React.SetStateAction<EditableSupplierRow>>,
  ) => {
    setForm((prev) => ({
      ...prev,
      crm_supplier: option?.label ?? "",
      supplier_id: option?.supplierId ?? null,
    }));
  };

  const optionForRow = (row: SupplierMapping): CrmOption | null =>
    row.crm_supplier
      ? { label: row.crm_supplier, value: row.supplier_id ?? row.crm_supplier, supplierId: row.supplier_id ?? null }
      : null;

  const handleRowClicked = (event: RowClickedEvent<SupplierMapping>) => {
    if (!event.data) return;

    const row = event.data;
    setSelectedRow(row);
    setEditForm({
      supplier_alias: row.supplier_alias ?? "",
      crm_supplier: row.crm_supplier ?? "",
      supplier_id: row.supplier_id ?? null,
      isPicklist: (row.isPicklist ?? "").toUpperCase() === "Y",
    });
    setSelectedCrm(optionForRow(row));
    onEditOpen();
  };

  const handleEditCancel = () => {
    setSelectedRow(null);
    setSelectedCrm(null);
    onEditClose();
  };

  const openAddModal = () => {
    setSelectedAddCrm(null);
    setAddForm(emptyForm());
    onAddOpen();
  };

  const handleAddCancel = () => {
    setSelectedAddCrm(null);
    onAddClose();
  };

  // Client-side pre-check mirrors the proc's own rules (which re-check server-side) so the
  // common mistakes get an immediate message without a round trip.
  const validationMessage = (form: EditableSupplierRow): string | null => {
    const alias = form.supplier_alias.trim();
    const crm = form.crm_supplier.trim();
    if (!alias) return "Supplier alias is required.";
    if (alias.length > SUPPLIER_ALIAS_MAX) return `Supplier alias exceeds ${SUPPLIER_ALIAS_MAX} characters.`;
    if (!crm) return "CRM supplier is required.";
    if (crm.length > CRM_SUPPLIER_MAX) return `CRM supplier exceeds ${CRM_SUPPLIER_MAX} characters.`;
    return null;
  };

  const toPayload = (form: EditableSupplierRow): SupplierMappingInput => ({
    supplier_alias: form.supplier_alias.trim(),
    crm_supplier: form.crm_supplier.trim(),
    supplier_id: form.supplier_id,
    isPicklist: form.isPicklist ? "Y" : "N",
  });

  const handleEditSave = async () => {
    if (!selectedRow) return;

    const problem = validationMessage(editForm);
    if (problem) {
      pushNotification(problem, "error");
      return;
    }

    try {
      setSaving(true);
      await updateSupplierMapping({ sk: selectedRow.sk, ...toPayload(editForm) });
      await loadSupplierMappings();
      pushNotification("Supplier mapping updated.", "success");
      handleEditCancel();
    } catch (err) {
      console.error("Error updating supplier mapping", err);
      // The backend returns the proc's own message for input rejections (400) - show that.
      const message = (err as Error)?.message || "Failed to update supplier mapping.";
      setError(message);
      pushNotification(message, "error");
    } finally {
      setSaving(false);
    }
  };

  const handleAddSave = async () => {
    const problem = validationMessage(addForm);
    if (problem) {
      pushNotification(problem, "error");
      return;
    }

    try {
      setSaving(true);
      await addSupplierMapping(toPayload(addForm));
      await loadSupplierMappings();
      pushNotification("Supplier mapping added.", "success");
      handleAddCancel();
    } catch (err) {
      console.error("Error adding supplier mapping", err);
      const message = (err as Error)?.message || "Failed to add supplier mapping.";
      setError(message);
      pushNotification(message, "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <InternalUserRoute>
      <PersistentGridProvider gridKey="reference-crm-supplier-mapping-grid">
        <BaseTemplate fitToWindow={true}>
          <BaseTemplate.Header>
            <HStack>
              <PersistentGridResetButton />
              <PersistentGridClearFiltersButton />
              <Button onClick={openAddModal}>Add</Button>
              {error && <Text color="red.500">{error}</Text>}
            </HStack>
          </BaseTemplate.Header>
          <BaseTemplate.Body>
            <Box w="100%" h="100%">
              <div className="ag-theme-quartz" style={{ width: "100%", height: "100%" }}>
                <PersistentGrid<SupplierMapping>
                  rowData={rowData}
                  columnDefs={columnDefs}
                  defaultColDef={defaultColDef}
                  loading={loading}
                  animateRows
                  onRowClicked={handleRowClicked}
                  getRowStyle={() => ({ cursor: "pointer" })}
                />
              </div>
            </Box>
          </BaseTemplate.Body>
        </BaseTemplate>
      </PersistentGridProvider>

      <Modal isOpen={isEditOpen} onClose={handleEditCancel}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Edit Supplier Mapping</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <SupplierMappingFields
              idSuffix="Edit"
              form={editForm}
              onAliasChange={(e) => setEditForm((prev) => ({ ...prev, supplier_alias: e.target.value }))}
              onPicklistChange={(checked) =>
                setEditForm((prev) => ({ ...prev, isPicklist: checked }))
              }
              selectedCrm={selectedCrm}
              onCrmChange={(option) => {
                setSelectedCrm(option);
                applyCrmChoice(option, setEditForm);
              }}
              loadCrmSupplierOptions={loadCrmSupplierOptions}
            />
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={handleEditCancel} isDisabled={saving}>
              Cancel
            </Button>
            <Button colorScheme="blue" onClick={handleEditSave} isLoading={saving}>
              Save
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={isAddOpen} onClose={handleAddCancel}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Add Supplier Mapping</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <SupplierMappingFields
              idSuffix="Add"
              form={addForm}
              onAliasChange={(e) => setAddForm((prev) => ({ ...prev, supplier_alias: e.target.value }))}
              onPicklistChange={(checked) =>
                setAddForm((prev) => ({ ...prev, isPicklist: checked }))
              }
              selectedCrm={selectedAddCrm}
              onCrmChange={(option) => {
                setSelectedAddCrm(option);
                applyCrmChoice(option, setAddForm);
              }}
              loadCrmSupplierOptions={loadCrmSupplierOptions}
            />
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={handleAddCancel} isDisabled={saving}>
              Cancel
            </Button>
            <Button colorScheme="blue" onClick={handleAddSave} isLoading={saving}>
              Save
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </InternalUserRoute>
  );
}

export default SupplierMappingPage;
